import {
  useDebounce,
  useOnChange,
  useStableCallback
} from "@prismicio/editor-support/React"
import {
  Box,
  FileUploadButton,
  FormSearchInput,
  Portal,
  Tooltip
} from "@prismicio/editor-ui"
import { Fragment, type ReactNode, type RefObject, useState } from "react"

import { textConstants } from "../constants/text"
import { useMediaLibrary } from "./MediaLibraryContext"

export function MediaLibraryActions() {
  const { actionContainerRef, isUploading, onFilesUpload } = useMediaLibrary()

  const hasActionsContainer = actionContainerRef?.current

  return (
    <MediaLibraryActionsContainer actionContainerRef={actionContainerRef}>
      <Box width="100%" gap={16}>
        <SearchBar />
        <Tooltip
          title={textConstants.warning.uploadWhileUploading.title}
          content={textConstants.warning.uploadWhileUploading.description}
          visible={isUploading}
          stableMount
        >
          <FileUploadButton
            disabled={isUploading}
            onFilesSelected={onFilesUpload}
            startIcon="cloudUpload"
            color={hasActionsContainer ? "purple" : "grey"}
            size="large"
          >
            Upload media
          </FileUploadButton>
        </Tooltip>
      </Box>
    </MediaLibraryActionsContainer>
  )
}

function SearchBar() {
  const { keyword, onKeywordChange, isFetchingMedia } = useMediaLibrary()

  const [inputValue, setInputValue] = useState(keyword)

  // Respect a reset request
  useOnChange(keyword, () => {
    if (keyword === "") setInputValue("")
  })

  useDebounce(
    inputValue,
    300,
    useStableCallback((inputValue) => onKeywordChange(inputValue))
  )

  return (
    <Box width="100%" flexDirection="column">
      <FormSearchInput
        value={inputValue}
        placeholder="Search"
        onValueChange={setInputValue}
        maxLength={200}
        isLoading={isFetchingMedia && keyword !== inputValue}
      />
    </Box>
  )
}

interface MediaLibraryActionsContainerProps {
  children: ReactNode
  actionContainerRef: RefObject<HTMLDivElement> | undefined
}

export function MediaLibraryActionsContainer(
  props: MediaLibraryActionsContainerProps
) {
  const { children, actionContainerRef } = props

  const hasActionsContainer = actionContainerRef?.current
  const Container = hasActionsContainer ? Portal : Fragment

  const containerProps = hasActionsContainer
    ? { container: actionContainerRef.current, asChild: true }
    : {}

  return <Container {...containerProps}>{children}</Container>
}
